/**
 * useAppLifecycle — All App-level side-effects consolidated into one hook.
 *
 * Handles: Auth state changes, SW updates, version polling, session refresh,
 * maintenance mode, beta flags, language changes, online/offline detection.
 *
 * Pure side-effect hook — returns nothing, operates via Zustand stores.
 */
import { useEffect, useRef } from 'react';
import { useUIStore, useUserStore } from '../stores';
import { ApiClient } from '../services/apiClient';
import { supabase } from '../services/supabaseClient';
import { setSentryUser } from '../index';
import logger from '../services/logger';
import i18n from '../i18n';
import { runIntegrityCheckAndAutoFix } from '../services/languageIntegrity';
import { trackEvent } from '../services/analytics';
import { setAnalyticsConsent } from '../services/analytics-ga4';
import { ensureIdbSchemaCompatible } from '../services/idbSchema';

export function useAppLifecycle() {
  const user = useUserStore((s) => s.user);
  const uiActions = useRef(useUIStore.getState());
  const userActions = useRef(useUserStore.getState());

  // ── IDB Schema Compatibility ────────────────────────────────
  // F-14: Guard against stale-cache-vs-new-code corruption. Runs once
  // per page load, before anything else touches IDB. No-op when schema
  // matches (fast path); wipes Fahryo caches when bumped.
  useEffect(() => {
    ensureIdbSchemaCompatible();
  }, []);

  // ── Auth State Change + Initial Session ──────────────────────
  useEffect(() => {
    let questionsLoaded = false;

    const loadQuestions = async (u: ReturnType<typeof ApiClient.getUser>, retryAttempt = 0) => {
      if (questionsLoaded) return;
      questionsLoaded = true;
      uiActions.current.setIsLoadingQuestions(true);
      try {
        const lang = u.language || 'de';
        await Promise.all([
          ApiClient.fetchQuestionsFromSupabase(lang),
          ApiClient.fetchChapterEssences(lang).then(() => {
            userActions.current.bumpEssencesVersion();
          }),
        ]);
        const questions = ApiClient.getAllQuestions();
        if (questions.length > 0) {
          await runIntegrityCheckAndAutoFix(
            questions,
            async (l) => {
              await ApiClient.fetchQuestionsFromSupabase(l, true);
            },
            lang
          );
        }
      } catch (e) {
        logger.error('App', `Question fetch failed (attempt ${retryAttempt + 1}/3):`, e);
        if (retryAttempt < 2 && navigator.onLine) {
          const delay = Math.pow(2, retryAttempt) * 2000;
          logger.info('App', `Retrying question load in ${delay}ms...`);
          questionsLoaded = false;
          setTimeout(() => loadQuestions(u, retryAttempt + 1), delay);
          return;
        }
      } finally {
        uiActions.current.setIsLoadingQuestions(false);
      }
    };

    // Track which session id we've already synced to avoid running the
    // SIGNED_IN handler twice for the same session (INITIAL_SESSION fires
    // right after listener registration with the current session, then
    // SIGNED_IN fires when the auto-exchange of `?code=…` completes —
    // depending on timing they can both arrive with the same session).
    let lastSyncedSessionId: string | null = null;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // DIAGNOSTIC (temporary, warn-level so it surfaces in production):
      // Trace every auth event so we can see what actually fires and with
      // what session payload — needed to debug the OAuth-PKCE-private-mode
      // login loop. Remove once that flow is confirmed working.
      logger.warn(
        'Auth',
        `event=${event} session.user=${session?.user?.id || 'null'} url=${window.location.search || '(no query)'}`
      );

      // Both INITIAL_SESSION and SIGNED_IN deliver a fresh session that we
      // need to sync. INITIAL_SESSION matters specifically for OAuth PKCE
      // callback in private browsing, where Supabase's auto code-exchange
      // may complete before the listener is registered — without this,
      // SIGNED_IN never fires and the app stays on AuthView.
      const isFreshSession =
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') &&
        session?.user?.id &&
        session.user.id !== lastSyncedSessionId;

      if (isFreshSession) {
        logger.warn('Auth', `syncing fresh session, userId=${session.user.id}`);
        lastSyncedSessionId = session.user.id;
        const { dbReady } = await ApiClient.syncSupabaseSession(session);
        const u = ApiClient.getUser();
        logger.warn(
          'Auth',
          `sync complete: dbReady=${dbReady}, hasSeenOnboarding=${u.hasSeenOnboarding}, lastSeenWhatsNew=${u.lastSeenWhatsNew}`
        );
        userActions.current.setUser(u);
        // AUTH-FIX: Clear authLoading now that we have a synced user
        uiActions.current.setAuthLoading(false);
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
          isPremium: u.isPremium,
          language: u.language,
        });
        // Replay the user's prior consent decision into Consent Mode v2 so
        // GA4 starts collecting on subsequent visits without waiting for a
        // re-click. shareDataWithAI is the "full" consent flag.
        if (u.consentGiven === true) {
          setAnalyticsConsent(u.shareDataWithAI === true);
        }
        if (u.isDarkMode) {
          document.documentElement.classList.add('dark');
        }
        if (u.language && u.language !== i18n.language && u.hasSeenOnboarding) {
          i18n.changeLanguage(u.language);
        }

        // AUTH-FIX v2: Only trust hasSeenOnboarding when DB data was loaded.
        // If DB timed out, the minimal-user inherits INITIAL_USER defaults
        // (hasSeenOnboarding=false) which would falsely trigger onboarding
        // for returning users.
        const decideOnboarding = (
          userObj: ReturnType<typeof ApiClient.getUser>,
          source: string
        ) => {
          logger.warn(
            'Auth',
            `decideOnboarding(${source}): hasSeenOnboarding=${userObj.hasSeenOnboarding}`
          );
          if (!userObj.hasSeenOnboarding) {
            // CONV-01: First-time user → registration funnel event
            trackEvent('registration', { method: 'oauth', language: userObj.language });
            uiActions.current.setShowOnboarding(true);
            // DEDUP-FIX: Only send welcome email if we haven't already sent
            // one for this specific user.
            const welcomeKey = `fahryo_welcome_email_sent_${session.user.id}`;
            if (!localStorage.getItem(welcomeKey)) {
              localStorage.setItem(welcomeKey, new Date().toISOString());
              setTimeout(async () => {
                try {
                  const apiBase =
                    window.location.hostname === 'localhost' ? 'http://localhost:3000' : '';
                  await fetch(`${apiBase}/api/email/send`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${session.access_token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      slug: 'welcome',
                      email: session.user.email,
                      variables: {
                        user: {
                          first_name: userObj.name || session.user.user_metadata?.full_name || '',
                        },
                        links: { app_login: 'https://app.fahryo.de' },
                        support: { email: 'support@fahryo.de' },
                      },
                    }),
                  });
                } catch (err) {
                  logger.error('App', 'Welcome email (OAuth) failed:', err);
                }
              }, 3000);
            }
          } else {
            // CONV-02: Returning user → login activity event
            trackEvent('login', { language: userObj.language });

            // DSGVO-02: Check if consent re-confirmation is needed after AGB/Privacy update
            (async () => {
              try {
                const apiBase =
                  window.location.hostname === 'localhost' ? 'http://localhost:3000' : '';
                const consentRes = await fetch(`${apiBase}/api/user/consent-check`, {
                  headers: { Authorization: `Bearer ${session.access_token}` },
                });
                if (consentRes.ok) {
                  const consentData = await consentRes.json();
                  if (consentData.needsReconfirmation) {
                    userActions.current.setUser(ApiClient.updateUser({ consentGiven: null }));
                  }
                }
              } catch {
                /* Non-blocking */
              }
            })();
          }
        };

        if (dbReady) {
          // DB returned real data — trust hasSeenOnboarding immediately
          decideOnboarding(u, 'db-ready');
        } else {
          // DB timed out — minimal-user defaults are unreliable. Refresh async.
          logger.warn('Auth', 'DB timed out → refreshing user from Supabase to decide onboarding');
          ApiClient.refreshUserFromSupabase().then((freshUser) => {
            if (freshUser) {
              userActions.current.setUser(freshUser);
              decideOnboarding(freshUser, 'refresh-fallback');
            } else {
              // Total offline fallback — only show onboarding if localStorage
              // says the user truly hasn't seen it (and we have a session key,
              // meaning localStorage isn't completely empty/fresh)
              logger.warn('Auth', 'refresh failed too — offline fallback');
              decideOnboarding(u, 'offline-fallback');
            }
          });
        }

        loadQuestions(u);
        const params = new URLSearchParams(window.location.search);
        if (params.get('tab') === 'profile') {
          uiActions.current.setActiveTab('profile');
          window.history.replaceState(null, '', window.location.pathname);
        }
        if (params.get('page') === 'feedback') {
          uiActions.current.setShowBetaFeedback(true);
          window.history.replaceState(null, '', window.location.pathname);
        }
      } else if (event === 'SIGNED_OUT') {
        logger.warn('Auth', 'SIGNED_OUT received — clearing user state');
        userActions.current.setUser(null);
        setSentryUser(null);
        userActions.current.setSessionData(null);
        uiActions.current.setActiveTab('learn');
      }
    });

    if (ApiClient.hasSession()) {
      logger.warn('Auth', 'init: local fahryo_app_session found, restoring user from localStorage');
      const u = ApiClient.getUser();
      userActions.current.setUser(u);
      if (u.language && u.language !== i18n.language && u.hasSeenOnboarding) {
        i18n.changeLanguage(u.language);
      }
      if (u.isDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      loadQuestions(u);

      const params = new URLSearchParams(window.location.search);
      if (params.get('tab') === 'profile') {
        uiActions.current.setActiveTab('profile');
        window.history.replaceState(null, '', window.location.pathname);
      }
      if (params.get('page') === 'feedback') {
        uiActions.current.setShowBetaFeedback(true);
        window.history.replaceState(null, '', window.location.pathname);
      }

      // AUTH-FIX: Do NOT show onboarding from stale localStorage here.
      // Wait for refreshUserFromSupabase to get the real DB value first.

      ApiClient.refreshUserFromSupabase().then((freshUser) => {
        if (freshUser) {
          userActions.current.setUser(freshUser);
          logger.info('App', 'User data refreshed from Supabase');
          // AUTH-FIX: Decide onboarding only after DB refresh completes.
          // This prevents false-positive onboarding for existing users
          // whose localStorage was stale or cleared.
          if (!freshUser.hasSeenOnboarding) {
            uiActions.current.setShowOnboarding(true);
          }
        } else {
          // DB refresh failed (offline?) — fall back to localStorage value
          if (!u.hasSeenOnboarding) {
            uiActions.current.setShowOnboarding(true);
          }
        }
      });
    } else {
      // Safari iOS fix: Supabase may have a valid session internally (sb-*-auth-token)
      // even when our custom fahryo_app_session was lost (ITP, storage pressure, PWA restart).
      // Recover by checking Supabase's own session and syncing if present.
      // Also covers the OAuth PKCE callback case in private browsing where the
      // listener-based handler may miss a fast auto-exchange — if we land here
      // with `?code=…` in the URL, retry getSession a few times because the
      // exchange runs async and may not be done by our first call.
      const isOAuthCallback = new URLSearchParams(window.location.search).has('code');
      logger.warn(
        'Auth',
        `recovery: hasSession=false, isOAuthCallback=${isOAuthCallback}, starting getSession loop`
      );
      const recoverSession = async () => {
        const maxAttempts = isOAuthCallback ? 6 : 1; // ~3s window for code-exchange
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          try {
            const {
              data: { session },
            } = await supabase.auth.getSession();
            logger.warn(
              'Auth',
              `recovery attempt ${attempt + 1}/${maxAttempts}: session.user=${session?.user?.id || 'null'}`
            );
            if (session?.user) {
              if (session.user.id === lastSyncedSessionId) {
                logger.warn('Auth', `recovery: session already synced by listener, skipping`);
                uiActions.current.setAuthLoading(false);
                return; // listener already synced
              }
              lastSyncedSessionId = session.user.id;
              logger.warn('Auth', `recovery: syncing user ${session.user.id}`);
              const { dbReady } = await ApiClient.syncSupabaseSession(session);
              const u = ApiClient.getUser();
              logger.warn(
                'Auth',
                `recovery sync done: dbReady=${dbReady}, hasSeenOnboarding=${u.hasSeenOnboarding}`
              );
              userActions.current.setUser(u);
              uiActions.current.setAuthLoading(false);
              setSentryUser({
                id: session.user.id,
                email: session.user.email,
                isPremium: u.isPremium,
                language: u.language,
              });
              if (u.isDarkMode) document.documentElement.classList.add('dark');
              if (u.language && u.language !== i18n.language && u.hasSeenOnboarding) {
                i18n.changeLanguage(u.language);
              }
              loadQuestions(u);
              // AUTH-FIX v2: Only trust onboarding flag from DB, not minimal-user
              if (dbReady) {
                if (!u.hasSeenOnboarding) uiActions.current.setShowOnboarding(true);
              } else {
                // DB timed out — refresh async before deciding
                ApiClient.refreshUserFromSupabase().then((freshUser) => {
                  if (freshUser) {
                    userActions.current.setUser(freshUser);
                    if (!freshUser.hasSeenOnboarding) uiActions.current.setShowOnboarding(true);
                  } else if (!u.hasSeenOnboarding) {
                    uiActions.current.setShowOnboarding(true);
                  }
                });
              }
              return;
            }
          } catch (e) {
            logger.warn('App', `Session recovery attempt ${attempt + 1} failed:`, e);
          }
          if (attempt < maxAttempts - 1) {
            await new Promise((r) => setTimeout(r, 500));
          }
        }
        // AUTH-FIX: Clear authLoading even if recovery failed, so user
        // sees the login page instead of an infinite loading spinner.
        uiActions.current.setAuthLoading(false);
        if (isOAuthCallback) {
          logger.warn(
            'App',
            'OAuth callback ?code=… but no session after retries — exchange likely failed'
          );
        }
      };
      recoverSession();
    }

    return () => subscription.unsubscribe();
  }, []);

  // ── Auth Redirect Hash ──────────────────────────────────────
  useEffect(() => {
    if (!window.location.hash.includes('access_token=')) return;

    const handleAuthRedirect = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (error) logger.error('Auth', 'Hash redirect error:', error);
        if (session) {
          logger.info('Auth', 'Session from verification redirect:', session.user.email);
        }
      } catch (e) {
        logger.error('Auth', 'Redirect handling error:', e);
      } finally {
        window.history.replaceState(null, '', window.location.pathname);
        uiActions.current.setAuthLoading(false);
      }
    };
    handleAuthRedirect();
  }, []);

  // ── PWA Tab Param ───────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'learn') uiActions.current.setActiveTab('learn');
    else if (tab === 'coach') uiActions.current.setActiveTab('chat');
    else if (tab === 'ranking') uiActions.current.setActiveTab('leaderboard');
    if (tab) window.history.replaceState({}, '', '/');
  }, []);

  // ── Service Worker Update Toast ─────────────────────────────
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        uiActions.current.setShowUpdateToast(true);
        setTimeout(() => uiActions.current.setShowUpdateToast(false), 4000);
      });
    }
  }, []);

  // ── Beta Flag ───────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const fetchBetaFlag = async () => {
      try {
        const { data } = await supabase
          .from('feature_flags')
          .select('enabled, meta')
          .eq('key', 'show_beta_badge')
          .single();
        if (data) {
          uiActions.current.setShowBetaBadge(data.enabled);
          if (data.meta) uiActions.current.setBetaBannerMeta(data.meta as any);
        }
      } catch (e) {
        logger.error('App', 'Beta flag fetch failed:', e);
      }
    };
    fetchBetaFlag();
  }, [user]);

  // ── Maintenance Mode Polling ────────────────────────────────
  // Reads the maintenance flag from the server's cached /api/health endpoint
  // instead of querying Supabase's feature_flags table directly. Previously
  // every open client hit the DB every 30s; now the DB is read at most once
  // per health-cache TTL regardless of client count. Operator-initiated
  // maintenance doesn't need sub-minute reactivity, so we poll at the same
  // 3-min cadence as the version check (plus an immediate check on mount).
  useEffect(() => {
    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3000' : '';
    const checkMaintenance = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/health`, { cache: 'no-store' });
        const data = await res.json();
        uiActions.current.setMaintenanceMode(data?.maintenance === true);
      } catch (err) {
        logger.error('Fahryo', 'Maintenance check failed:', err);
      }
    };
    checkMaintenance();
    const interval = setInterval(checkMaintenance, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // ── Data Sync + Online/Offline ──────────────────────────────
  useEffect(() => {
    const handleSync = () => {
      const freshUser = ApiClient.getUser();
      if (user) {
        const oldBadgeIds = user.badges.map((b) => b.id);
        const newBadges = freshUser.badges.filter((b) => !oldBadgeIds.includes(b.id));
        if (newBadges.length > 0) {
          userActions.current.setNewBadge(newBadges[0]);
        }
      }
      userActions.current.setUser(freshUser);
    };

    const handleOnline = () => {
      uiActions.current.setIsOffline(false);
      uiActions.current.showToast(i18n.t('Wieder online — Daten werden synchronisiert'));
      ApiClient.syncPendingChanges().then((count) => {
        if (count > 0) {
          logger.info('App', `Synced ${count} pending changes`);
        }
      });
    };

    const handleOffline = () => uiActions.current.setIsOffline(true);

    const handleQuestionsUpdated = () => {
      logger.info('App', 'Questions updated from Supabase background refresh');
      uiActions.current.setIsLoadingQuestions(false);
    };

    window.addEventListener('fahryo-data-updated', handleSync);
    window.addEventListener('fahryo-questions-updated', handleQuestionsUpdated);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (navigator.onLine) {
      uiActions.current.setIsOffline(false);
    }

    return () => {
      window.removeEventListener('fahryo-data-updated', handleSync);
      window.removeEventListener('fahryo-questions-updated', handleQuestionsUpdated);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user]);

  // ── Auto-Update Version Polling ─────────────────────────────
  useEffect(() => {
    const APP_VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';
    if (!APP_VERSION) return;

    const forceUpdate = async () => {
      // F-01: never rip a running exam/learn session apart. Defer the hard
      // reload until the session ends — same guard the SW update prompt
      // uses (client/index.tsx), applied via the same pending-update hook
      // that useQuestionSession fires on complete/exit.
      const sessionActive = document.querySelector('[data-session-active="true"]');
      if (sessionActive) {
        const win = window as unknown as { __FAHRYO_PENDING_UPDATE?: () => void };
        if (!win.__FAHRYO_PENDING_UPDATE) {
          logger.info('App', 'New version but session active — deferring forced update');
          win.__FAHRYO_PENDING_UPDATE = () => {
            forceUpdate();
          };
        }
        return;
      }
      logger.info('App', 'Force-updating: clearing SW + caches...');
      try {
        const registrations = (await navigator.serviceWorker?.getRegistrations()) || [];
        await Promise.all(registrations.map((r) => r.unregister()));
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
        logger.info('App', 'SW unregistered + caches cleared. Reloading...');
      } catch (e) {
        logger.error('App', 'Cache cleanup failed:', e);
      }
      window.location.reload();
    };

    const checkVersion = async () => {
      try {
        const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3000' : '';
        const res = await fetch(`${baseUrl}/api/health`, { cache: 'no-store' });
        const data = await res.json();
        if (data.version && data.version !== APP_VERSION) {
          logger.info('App', `New version: ${data.version} (current: ${APP_VERSION}). Updating...`);
          await forceUpdate();
        }
      } catch (err) {
        logger.warn('App', 'Version check failed (network):', err);
      }
    };

    const interval = setInterval(checkVersion, 3 * 60 * 1000);
    const initial = setTimeout(checkVersion, 10000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      clearTimeout(initial);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // ── Session Refresh on Tab Focus ────────────────────────────
  useEffect(() => {
    let lastVisible = Date.now();

    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !user) return;
      const away = Date.now() - lastVisible;
      lastVisible = Date.now();

      if (away > 5 * 60 * 1000) {
        logger.info(
          'App',
          `User returned after ${Math.round(away / 60000)}min — refreshing session`
        );
        supabase.auth.refreshSession().then(({ error }) => {
          if (error) {
            logger.warn('App', 'Session refresh failed:', error.message);
          } else {
            logger.info('App', 'Session refreshed successfully');
          }
        });
        ApiClient.refreshUserFromSupabase().then((freshUser) => {
          if (freshUser) userActions.current.setUser(freshUser);
        });
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user]);

  // ── Language Change Handler ─────────────────────────────────
  useEffect(() => {
    const handleLangChange = async (e: Event) => {
      const lang = (e as CustomEvent).detail?.language || 'de';
      uiActions.current.setIsLoadingQuestions(true);
      try {
        await Promise.all([
          ApiClient.fetchQuestionsFromSupabase(lang, true),
          ApiClient.fetchChapterEssences(lang).then(() => {
            userActions.current.bumpEssencesVersion();
          }),
        ]);
        const questions = ApiClient.getAllQuestions();
        if (questions.length > 0) {
          await runIntegrityCheckAndAutoFix(
            questions,
            async (l) => {
              await ApiClient.fetchQuestionsFromSupabase(l, true);
            },
            lang
          );
        }
      } catch (err) {
        logger.error('App', 'Re-fetch questions after lang change failed:', err);
      } finally {
        uiActions.current.setIsLoadingQuestions(false);
        userActions.current.setUser(ApiClient.getUser());
      }
    };
    window.addEventListener('fahryo-language-changed', handleLangChange);
    return () => window.removeEventListener('fahryo-language-changed', handleLangChange);
  }, []);

  // ── Loading Safety Timeout ──────────────────────────────────
  const isLoadingQuestions = useUIStore((s) => s.isLoadingQuestions);
  useEffect(() => {
    if (!isLoadingQuestions) return;
    const timer = setTimeout(() => {
      uiActions.current.setIsLoadingQuestions(false);
      logger.warn('App', 'Loading safety timeout — showing app without full question load');
    }, 10000);
    return () => clearTimeout(timer);
  }, [isLoadingQuestions]);
}
